// apps/storefront/src/lib/consent.ts
//
// Storage for the visitor's privacy choice — storage only, not state. The
// in-memory copy that makes a refused write still take effect for this page
// view lives in privacyRuntime.ts; the rules live in privacyDecision.ts.
//
// The choice is a first-party cookie (`sf_privacy`, see privacyDecision.ts for
// the format and why it is a cookie): route handlers must be able to read it.
// Cookies are per host, and every tenant storefront is its own host, so the
// choice is per tenant without a tenant id in the name.
//
// When the browser refuses the cookie (a SameSite=Lax cookie written from a
// cross-site iframe — an embedded storefront — or blocked site data), the same
// encoded value goes to localStorage `sifly_privacy_<tenantId>` instead, which
// partitioned storage still allows. Only the browser can read that copy; the
// server then sees "no choice".
//
// The previous shape — localStorage `sifly_consent_<tenantId>`,
// `{version:1, ts, analytics, marketing, source}` — is live in real browsers,
// so it is MIGRATED on read rather than ignored: ignoring it would silently
// re-enable tracking for someone who declined.
//
// SSR-safe: every function guards `typeof window`. Merely ACCESSING
// window.localStorage throws a SecurityError in third-party iframe contexts
// with blocked site data, so that access sits inside a try (an unguarded read
// crashed hydration for embedded storefront pages, found 2026-07-04).

import {
  PREFERENCE_TTL_DAYS,
  PRIVACY_COOKIE,
  encodePreference,
  isExpired,
  parsePreference,
  readCookieValue,
  type Purpose,
  type PurposeDecision,
  type SavedPreference,
} from './privacyDecision'

export const CONSENT_UPDATED_EVENT = 'sifly:consent-updated'
export const OPEN_PREFS_EVENT = 'sifly:open-consent-prefs'

/** The pre-2026-10 localStorage key. Read only, to migrate it. */
export function legacyConsentKey(tenantId: string): string {
  return `sifly_consent_${tenantId}`
}

/** The localStorage fallback for a refused cookie (see header). */
export function fallbackKey(tenantId: string): string {
  return `sifly_privacy_${tenantId}`
}

/** `navigator.globalPrivacyControl`, exactly — a truthy string is not a signal. */
export function readGpc(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl === true
  } catch {
    return false
  }
}

function readCookieString(): string {
  try {
    return document.cookie
  } catch {
    return '' // blocked (sandboxed iframe) → no stored choice
  }
}

type LegacyV1 = { version: 1; ts: number; analytics?: boolean; marketing?: boolean }

function migrateLegacy(v1: LegacyV1): SavedPreference {
  const analytics: PurposeDecision = v1.analytics === true ? 'allow' : 'deny'
  const marketing: PurposeDecision = v1.marketing === true ? 'allow' : 'deny'
  return {
    version: 2,
    // Clarity moved from "analytics" to "marketing" in v2, so a v1 visitor who
    // allowed analytics but not marketing lands on session_replay = deny: the
    // stricter reading of what they agreed to.
    purposes: { analytics, measurement: analytics, advertising: marketing, session_replay: marketing },
    // The original decision time — migrating must not reset the 365-day TTL.
    updatedAt: v1.ts,
    // Unknown notice: a v1 allow grants nothing in consent mode until re-asked.
    // A v1 DENY still holds everywhere (the engine honours a deny regardless).
    noticeVersion: '',
    gpcOverride: false,
  }
}

function readLegacy(tenantId: string, now: number): SavedPreference | null {
  let parsed: Partial<LegacyV1> | null = null
  try {
    const raw = window.localStorage.getItem(legacyConsentKey(tenantId))
    if (!raw) return null
    parsed = JSON.parse(raw) as Partial<LegacyV1>
  } catch {
    return null
  }
  if (parsed?.version !== 1 || typeof parsed.ts !== 'number' || isExpired(parsed.ts, now)) return null
  return migrateLegacy(parsed as LegacyV1)
}

function readFallback(tenantId: string, now: number): SavedPreference | null {
  try {
    return parsePreference(window.localStorage.getItem(fallbackKey(tenantId)), now)
  } catch {
    return null
  }
}

export function readPreference(tenantId: string, now: number = Date.now()): SavedPreference | null {
  if (typeof window === 'undefined') return null
  const stored = parsePreference(readCookieValue(readCookieString(), PRIVACY_COOKIE), now)
  if (stored) return stored
  const fallback = readFallback(tenantId, now)
  if (fallback) return fallback
  const legacy = readLegacy(tenantId, now)
  // Re-store it in the new shape so the server sees the same choice. The old
  // record is removed ONLY once the new one is stored — otherwise a decline
  // would exist for this one read and then vanish, re-enabling tracking.
  if (legacy && store(tenantId, legacy)) {
    try {
      window.localStorage.removeItem(legacyConsentKey(tenantId))
    } catch {
      // Kept; it simply re-migrates on the next read.
    }
  }
  return legacy
}

function writeCookie(state: SavedPreference): boolean {
  const value = encodePreference(state)
  try {
    const secure = window.location?.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${PRIVACY_COOKIE}=${value}; Path=/; Max-Age=${PREFERENCE_TTL_DAYS * 86_400}; SameSite=Lax${secure}`
    // A blocked cookie write does not throw — it silently does nothing. Read
    // it back to know whether the choice actually persisted.
    return readCookieValue(readCookieString(), PRIVACY_COOKIE) === value
  } catch {
    return false
  }
}

/** Cookie first; localStorage when the cookie is refused. True if either stuck. */
function store(tenantId: string, state: SavedPreference): boolean {
  if (writeCookie(state)) {
    try {
      window.localStorage.removeItem(fallbackKey(tenantId))
    } catch {
      // nothing to clear
    }
    return true
  }
  try {
    window.localStorage.setItem(fallbackKey(tenantId), encodePreference(state))
    return true
  } catch {
    return false
  }
}

export type PreferenceWrite = {
  state: SavedPreference
  /**
   * False when the browser refused BOTH the cookie and the localStorage
   * fallback (blocked site data). Nothing was stored, so a re-read answers
   * null and a reload would lose the choice — the caller must act on it.
   */
  persisted: boolean
}

/**
 * Stores the visitor's choice. Deliberately does NOT announce it: listeners
 * run synchronously inside dispatchEvent and re-read the verdict, so the
 * caller needs a window between the write and the announcement to put its
 * in-memory copy in place (privacyRuntime.savePrivacyChoice).
 */
export function writePreference(
  tenantId: string,
  purposes: Partial<Record<Purpose, PurposeDecision>>,
  noticeVersion: string,
  gpcOverride: boolean,
  now: number = Date.now(),
): PreferenceWrite {
  const state: SavedPreference = { version: 2, purposes, updatedAt: now, noticeVersion, gpcOverride }
  if (typeof window === 'undefined') return { state, persisted: false }
  return { state, persisted: store(tenantId, state) }
}

/** Tells everything on the page the choice changed, so vendors can be revoked in-process. */
export function announcePreferenceChange(persisted: boolean): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(CONSENT_UPDATED_EVENT, { detail: { persisted } }))
}
